import type { PlayerBaseOptions } from '@racemap/sdk/schema/player';
import { NULL_TAG } from '@racemap/utilities/consts/events';
import type { UnitType } from '@racemap/utilities/types/units';
import update, { type Spec } from 'immutability-helper';
import { clamp, isEqual } from 'lodash';
import { unstable_batchedUpdates } from 'react-dom';
import type { SetState } from 'zustand';
import { SHADOW_TRACK_STEP, TIME_MODES } from './constants';
import type { HashContent } from './controller/HashManager';
import type { EventStateWithOptions } from './parse';
import {
  getCurrentEvents,
  reduceEndTime,
  reduceMax,
  reduceMin,
  reduceStartTime,
} from './selectors';
import {
  type EventState,
  type FlagContent,
  type GeolocationStatus,
  type LatLngOpacity,
  MapType,
  PinToShadowTrackMode,
  type Point,
  type StarterType,
  type State,
  type TimeMode,
} from './store';
import { difference, iteratorFind, toggleThroughArray, union } from './utils';
import { correctedNow } from './utils/clock';

const EPSILON = 100;

function cleanEventIndices(eventCount: number, eventIndices: Array<number>) {
  return [
    ...new Set(eventIndices.filter((a) => !Number.isNaN(a) && a >= 0 && a < eventCount)),
  ].sort();
}

function setTimerTime(state: State, time: number): State {
  const currentEvents = getCurrentEvents(state);
  const currentStartTime = reduceStartTime(currentEvents);
  const currentEndTime = reduceEndTime(currentEvents);

  const liveMark = correctedNow() - reduceMax(currentEvents, (ev) => ev.options.liveDelay);

  const isLive = time >= liveMark - EPSILON;
  let newTime = Math.min(liveMark, time, currentEndTime);

  if (currentStartTime <= correctedNow()) {
    newTime = clamp(newTime, currentStartTime, currentEndTime);
  }

  if (state.timer.time === newTime && state.timer.isLive === isLive) {
    return state;
  }

  return update(state, {
    timer: {
      $merge: {
        time: newTime,
        isLive,
      },
    },
  });
}

function switchEvent(state: State, currentEventIndices: Array<number>): State {
  let cleanedCurrentEventIndices = cleanEventIndices(state.events.length, currentEventIndices);
  if (cleanedCurrentEventIndices.length === 0) {
    cleanedCurrentEventIndices = getDefaultCurrentEventIds(state.events);
  }
  if (!isEqual(cleanedCurrentEventIndices, state.currentEventIndices)) {
    const currentEvents = cleanedCurrentEventIndices.map((i) => state.events[i]);
    const selectedIds = state.timer.selected.filter((selectedId) =>
      currentEvents.some(
        (ev) =>
          ev.starters.some((s) => s.id === selectedId) ||
          ev.pois.some((p) => p.id === selectedId) ||
          ev.tracks.some((t) => t.id === selectedId),
      ),
    );
    return setTimerTime(
      update(state, {
        currentEventIndices: { $set: cleanedCurrentEventIndices },
        timer: { selected: { $set: selectedIds } },
      }),
      state.timer.time,
    );
  }
  return state;
}

function getAvailableTagHashes(state: State): Set<string> {
  const output = new Set<string>();
  for (const event of state.events) {
    for (const starter of event.starters) {
      for (const [key, value] of starter.tags) {
        output.add(`${key}:${value}`);
        output.add(`${key}:${NULL_TAG}`);
      }
    }
  }
  return output;
}

function flattenTags(tagMap: Map<string, Array<string>>): Array<[string, string]> {
  const output: Array<[string, string]> = [];
  for (const [key, values] of tagMap) {
    for (const value of values) {
      output.push([key, value]);
    }
  }
  return output;
}

function unflattenTags(tagList: Array<[string, string]>): Map<string, Array<string>> {
  const output: Map<string, Array<string>> = new Map();
  for (const [key, value] of tagList) {
    if (output.has(key)) {
      output.get(key)?.push(value);
    } else {
      output.set(key, [value]);
    }
  }
  return output;
}

function onlyAvailableTagGroups(
  selectedTags: Map<string, Array<string>>,
  availableTags: Set<string>,
): Map<string, Array<string>> {
  return unflattenTags(
    flattenTags(selectedTags).filter(([key, value]) => availableTags.has(`${key}:${value}`)),
  );
}

function setForEvents(newState: State, updater: Spec<EventState, never>): State {
  return update(newState, {
    events: {
      $set: newState.events.map((event) => update(event, updater)),
    },
  });
}

function applyHashOverrides(state: State, hash: HashContent): State {
  let newState = state;
  // Applying override from hash
  if (hash.showAllFlags != null) {
    newState = update(newState, { options: { showAllFlags: { $set: hash.showAllFlags } } });
  }
  if (hash.largeMode != null) {
    newState = update(newState, { options: { largeMode: { $set: hash.largeMode } } });
  }
  if (hash.flagContent != null) {
    newState = update(newState, {
      options: { overrideFlagContent: { $set: hash.flagContent } },
    });
  }
  if (hash.selectedFlagContent != null) {
    newState = update(newState, {
      options: { overrideSelectedFlagContent: { $set: hash.selectedFlagContent } },
    });
  }
  if (hash.listOpen != null) {
    newState = setForEvents(newState, {
      options: { listOpenByDefault: { $set: hash.listOpen } },
    });
  }
  if (hash.infoTabsDefaultTab != null) {
    newState = setForEvents(newState, {
      options: { infoTabs: { defaultTab: { $set: hash.infoTabsDefaultTab } } },
    });
  }
  if (hash.infoTabsDisabledTabs != null) {
    newState = setForEvents(newState, {
      options: { infoTabs: { disabledTabs: { $set: hash.infoTabsDisabledTabs } } },
    });
  }
  if (hash.infoTabsOpenByDefault != null) {
    newState = setForEvents(newState, {
      options: { infoTabs: { openByDefault: { $set: hash.infoTabsOpenByDefault } } },
    });
  }
  if (hash.hideNonSelected === true) {
    let selectedIds: Array<string> = [];
    if (hash.selected != null) {
      selectedIds = hash.selected;
    }
    if (hash.selectedStartNumber != null) {
      selectedIds = selectedIds.concat(
        hash.selectedStartNumber
          .map((startNumber) =>
            iteratorFind(
              newState.startersById.entries(),
              ([, starters]: [string, Array<StarterType>]) =>
                starters.some((starter: StarterType) => starter.startNumber === startNumber),
            ),
          )
          .filter((item) => item != null)
          .map(([starterId]) => starterId),
      );
    }
    newState = update(newState, {
      timer: {
        hidden: {
          $set: new Set(
            Array.from(newState.startersById.keys()).filter((id) => !selectedIds.includes(id)),
          ),
        },
      },
    });
  }
  if (hash.mapStyle != null) {
    newState = update(newState, {
      options: {
        mapType: { $set: MapType[hash.mapStyle.toUpperCase() as keyof typeof MapType] },
      },
    });
  } else if (newState.options.mapType === MapType.ROADMAP) {
    newState = update(newState, {
      options: { mapType: { $set: MapType.TERRAIN } },
    });
  }
  if (hash.zoom != null) {
    newState = update(newState, {
      options: { overrideZoom: { $set: hash.zoom } },
    });
  }
  if (hash.viewport != null) {
    newState = update(newState, {
      options: { overrideViewport: { $set: hash.viewport } },
    });
  }
  return newState;
}

function setSelectedTags(
  state: State,
  selectedTags: Map<string, Array<string>>,
  { overwriteDefault = false }: { overwriteDefault?: boolean } = {},
): State {
  const availableTags = getAvailableTagHashes(state);
  const onlyAvailableSelectedTags = onlyAvailableTagGroups(selectedTags, availableTags);

  return update(state, {
    timer: {
      selectedTags: {
        $set: onlyAvailableSelectedTags,
      },
      // overwrite default selected tags if specified, otherwise keep the existing default selected tags
      ...(overwriteDefault
        ? {
            defaultSelectedTags: {
              $set: onlyAvailableSelectedTags,
            },
          }
        : {}),
    },
  });
}

export function getDefaultCurrentEventIds(events: Array<EventState>): Array<number> {
  const currentlyRunningEvents = events.filter(
    (ev) => correctedNow() >= ev.startTime && ev.endTime >= correctedNow(),
  );
  if (currentlyRunningEvents.length > 0) {
    return currentlyRunningEvents.map((ev) => ev.index);
  }
  // Show events that start earliest (can be multiple events that start at the same time)
  const sortedEvents = events.slice().sort((a, b) => a.startTime - b.startTime);
  return sortedEvents.flatMap((ev, i) => (ev.startTime === sortedEvents[0].startTime ? [i] : []));
}

export const actions = (set: SetState<State>) => {
  function batch(func: (state: State) => State) {
    unstable_batchedUpdates(() => {
      set((state) => func(state));
    });
  }

  return {
    receiveBaseData(
      newEvents: Array<EventStateWithOptions>,
      newCurrentEventIndices: Array<number>,
      hash: HashContent,
    ) {
      if (newEvents.length < 1) return;
      let currentEventIndices = cleanEventIndices(newEvents.length, newCurrentEventIndices);
      if (currentEventIndices.length === 0) {
        currentEventIndices = getDefaultCurrentEventIds(newEvents);
      }
      const eventOptions = newEvents[0].globalOptions;
      const currentEvents = currentEventIndices.map((index) => newEvents[index]);
      const isLive = correctedNow() <= reduceEndTime(currentEvents);
      const time = isLive
        ? correctedNow() - reduceMin(currentEvents, (ev) => ev.options.liveDelay)
        : reduceStartTime(currentEvents);

      const startersById = new Map();
      for (const event of newEvents) {
        for (const starter of event.starters) {
          const entry = startersById.get(starter.id);
          if (entry != null) {
            entry.push(starter);
          } else {
            startersById.set(starter.id, [starter]);
          }
        }
      }

      const events = newEvents.map(({ changeable, globalOptions: eventOptions, ...ev }) => ev);
      const eventsChangeable = newEvents.map(({ changeable }) => changeable);
      const defaultSelectedTags = new Map(
        Object.entries(eventOptions.tagCategoriesConfig).map(([key, value]) => [
          key,
          value.default,
        ]),
      );

      batch((state) => {
        let newState = update(state, {
          currentEventIndices: { $set: currentEventIndices },
          events: { $set: events },
          eventsChangeable: { $set: eventsChangeable },
          startersById: { $set: startersById },
          timer: {
            $merge: {
              isLive,
              isPlaying: isLive,
            },
          },
          options: {
            mapType: { $set: eventOptions.mapType },
            unitType: { $set: eventOptions.unitType },
            timeMode: { $set: eventOptions.timeMode },
            showAllFlags: { $set: eventOptions.showAllFlags },
            pinToShadowTrack: { $set: eventOptions.pinToShadowTrack },
            largeMode: { $set: eventOptions.largeMode },
          },
        });
        newState = applyHashOverrides(newState, hash);
        newState = setTimerTime(newState, time);
        newState = setSelectedTags(newState, defaultSelectedTags, { overwriteDefault: true });

        return newState;
      });
    },

    receiveOptions(options: PlayerBaseOptions, hash: HashContent) {
      batch((state) => {
        let newState = update(state, {
          eventId: { $set: options.eventId },
          options: { $merge: options },
        });
        newState = applyHashOverrides(newState, hash);
        return newState;
      });
    },

    refreshFullEventData(newEvents: Array<EventStateWithOptions>) {
      if (newEvents.length < 1) return;

      const startersById = new Map();
      for (const event of newEvents) {
        for (const starter of event.starters) {
          const entry = startersById.get(starter.id);
          if (entry != null) {
            entry.push(starter);
          } else {
            startersById.set(starter.id, [starter]);
          }
        }
      }

      const events = newEvents.map(({ changeable, globalOptions, ...ev }) => ev);
      const eventsChangeable = newEvents.map(({ changeable }) => changeable);

      batch((state) => {
        return update(state, {
          events: { $set: events },
          eventsChangeable: { $set: eventsChangeable },
          startersById: { $set: startersById },
        });
      });
    },

    receiveStarterTracks(eventId: string, starterTracks: Map<string, Array<Point>>) {
      batch((state) => {
        const eventIndex = state.eventsChangeable.findIndex((ev) => ev.eventId === eventId);
        if (eventIndex >= 0) {
          return update(state, {
            eventsChangeable: { [eventIndex]: { starterTracks: { $set: starterTracks } } },
          });
        }
        return state;
      });
    },

    switchEvent(currentEventIndices: Array<number>) {
      batch((state) => switchEvent(state, currentEventIndices));
    },

    toggleSelectedEvent(eventIndex: number) {
      batch((state) => {
        if (state.currentEventIndices.includes(eventIndex)) {
          const newCurrentEventIndices = state.currentEventIndices.filter(
            (index) => index !== eventIndex,
          );
          if (newCurrentEventIndices.length > 0) {
            return switchEvent(state, newCurrentEventIndices);
          }
        } else {
          return switchEvent(state, [...state.currentEventIndices, eventIndex]);
        }
        return state;
      });
    },

    setHighlighted(highlighted: string | null) {
      batch((state) =>
        highlighted == null ||
        state.startersById.has(highlighted) ||
        state.events.some((ev) => ev.pois.some((p) => p.id === highlighted)) ||
        state.events.some((ev) => ev.tracks.some((p) => p.id === highlighted))
          ? update(state, {
              timer: { highlighted: { $set: highlighted } },
            })
          : state,
      );
    },

    setHighlightedOffset(highlightedOffset: number) {
      batch((state) => {
        const currentEventIndex = state.currentEventIndices[0];
        const currentEvent =
          currentEventIndex != null ? state.events[currentEventIndex] : undefined;
        const shadowTrack = currentEvent?.chunkedShadowTrack;

        if (highlightedOffset >= 0 && shadowTrack != null) {
          const maxOffset = Math.max(0, shadowTrack.size() - 1) * SHADOW_TRACK_STEP;

          return update(state, {
            timer: {
              highlightedOffset: {
                $set: clamp(highlightedOffset, 0, maxOffset),
              },
            },
          });
        }
        return update(state, {
          timer: { highlightedOffset: { $set: -1 } },
        });
      });
    },

    setSelected(selected: Array<string>) {
      batch((state) =>
        update(state, {
          timer: {
            selected: {
              $set: selected.filter(
                (s) =>
                  state.startersById.has(s) ||
                  state.events.some((ev) => ev.pois.some((poi) => poi.id === s)) ||
                  state.events.some((ev) => ev.tracks.some((track) => track.id === s)),
              ),
            },
          },
        }),
      );
    },

    setSelectedStartNumber(selectedStartNumbers: Array<string>) {
      batch((state) => {
        const currentSelected = state.timer.selected;
        const currentSelectedNonStarterIds = currentSelected.filter(
          (s) => !state.startersById.has(s),
        );
        const selectedStarterIds = selectedStartNumbers
          .map((startNumber) =>
            iteratorFind(
              state.startersById.entries(),
              ([, starters]: [string, Array<StarterType>]) =>
                starters.some((starter) => starter.startNumber === startNumber),
            ),
          )
          .filter((item) => item != null)
          .map(([starterId]) => starterId);

        return update(state, {
          timer: {
            selected: {
              $set: [...selectedStarterIds, ...currentSelectedNonStarterIds],
            },
          },
        });
      });
    },

    setSelectedTags(selectedTags: Map<string, Array<string>>) {
      batch((state) => setSelectedTags(state, selectedTags));
    },

    toggleSelectedStarter(starterId: string) {
      batch((state) => {
        const currentSelectedStarters = state.timer.selected;
        if (!currentSelectedStarters.includes(starterId)) {
          return update(state, {
            timer: { selected: { $push: [starterId] } },
          });
        }
        return update(state, {
          timer: {
            selected: {
              $set: currentSelectedStarters.filter((id) => id !== starterId),
            },
          },
        });
      });
    },

    toggleSelectedTag(key: string, value: string) {
      batch((state) => {
        const flatTags = flattenTags(state.timer.selectedTags);
        const tagIsSelected = flatTags.some((item) => item[0] === key && item[1] === value);
        if (!tagIsSelected) {
          return update(state, {
            timer: {
              selectedTags: {
                $set: unflattenTags([...flatTags, [key, value]]),
              },
            },
          });
        }
        return update(state, {
          timer: {
            selectedTags: {
              $set: unflattenTags(flatTags.filter((item) => item[0] !== key || item[1] !== value)),
            },
          },
        });
      });
    },

    hide(ids: Array<string>) {
      batch((state) =>
        update(state, {
          timer: { hidden: { $set: union(state.timer.hidden, new Set(ids)) } },
        }),
      );
    },

    unhide(ids: Array<string>) {
      batch((state) =>
        update(state, {
          timer: { hidden: { $set: difference(state.timer.hidden, new Set(ids)) } },
        }),
      );
    },
    toggleHidden(id: string) {
      batch((state) =>
        update(state, {
          timer: {
            hidden: {
              $set: state.timer.hidden.has(id)
                ? difference(state.timer.hidden, new Set([id]))
                : union(state.timer.hidden, new Set([id])),
            },
          },
        }),
      );
    },

    pauseTimer() {
      batch((state) => update(state, { timer: { isPlaying: { $set: false } } }));
    },

    resumeTimer() {
      batch((state) => update(state, { timer: { isPlaying: { $set: true } } }));
    },

    toggleTimer() {
      batch((state) => {
        if (reduceEndTime(getCurrentEvents(state)) <= state.timer.time && !state.timer.isPlaying) {
          return update(state, {
            timer: {
              $merge: {
                isPlaying: true,
                time: reduceStartTime(getCurrentEvents(state)),
              },
            },
          });
        }
        return update(state, {
          timer: { isPlaying: { $set: !state.timer.isPlaying } },
        });
      });
    },

    setIsLive(isLive: boolean) {
      batch((state) => update(state, { timer: { isLive: { $set: isLive } } }));
    },

    toggleTimerSpeed() {
      batch((state) => {
        const timerSpeeds = [1, 2, 4, 8, 16];
        const newSpeed = toggleThroughArray(timerSpeeds, state.timer.speed);
        return update(state, { timer: { speed: { $set: newSpeed } } });
      });
    },

    setTimerSpeed(speed: number) {
      batch((state) => {
        const timerSpeeds = [1, 2, 4, 8, 16];
        const newSpeed = timerSpeeds.includes(speed) ? speed : state.timer.speed;
        return update(state, { timer: { speed: { $set: newSpeed } } });
      });
    },

    setTimerTime(time: number) {
      batch((state) => setTimerTime(state, time));
    },

    /**
     * Records the camera on screen so the share dialog can put it into a link. Called once the map
     * settles, never per frame — see PlaybackPositionManager for why that matters.
     */
    setCurrentViewport(viewport: { longitude: number; latitude: number; zoom: number }) {
      batch((state) => update(state, { currentViewport: { $set: viewport } }));
    },

    forwardTimerTime(timeDiff: number) {
      batch((state) => {
        const currentEvents = getCurrentEvents(state);
        const endTime = reduceEndTime(currentEvents);
        if (state.timer.time >= endTime) {
          return update(state, { timer: { isPlaying: { $set: false } } });
        }
        const stepsPerMs =
          state.timer.speed * reduceMin(currentEvents, (ev) => ev.options.replaySpeedup);
        let newTime = state.timer.time + stepsPerMs * timeDiff;
        if (newTime > endTime) {
          newTime = endTime;
        }
        return setTimerTime(state, newTime);
      });
    },

    setGeolocationStatus(status: GeolocationStatus) {
      batch((state) => update(state, { timer: { geolocation: { status: { $set: status } } } }));
    },

    setOwnLocation(latlng: LatLngOpacity | null) {
      batch((state) => update(state, { timer: { geolocation: { latlng: { $set: latlng } } } }));
    },

    setMapType(mapType: MapType) {
      batch((state) => update(state, { options: { mapType: { $set: mapType } } }));
    },

    setUnitType(unitType: UnitType) {
      batch((state) => update(state, { options: { unitType: { $set: unitType } } }));
    },

    setShowAllFlags(showAllFlags: boolean) {
      batch((state) => update(state, { options: { showAllFlags: { $set: showAllFlags } } }));
    },

    setLargeMode(largeMode: boolean) {
      batch((state) => update(state, { options: { largeMode: { $set: largeMode } } }));
    },

    setOverrideFlagContent(overrideFlagContent: null | FlagContent) {
      batch((state) =>
        update(state, { options: { overrideFlagContent: { $set: overrideFlagContent } } }),
      );
    },

    setTimeMode(timeMode: TimeMode) {
      batch((state) => update(state, { options: { timeMode: { $set: timeMode } } }));
    },

    toggleTimeMode() {
      batch((state) =>
        update(state, {
          options: {
            timeMode: {
              $set: toggleThroughArray(TIME_MODES, state.options.timeMode),
            },
          },
        }),
      );
    },

    setPinToShadowTrack(pinToShadowTrack: PinToShadowTrackMode) {
      batch((state) =>
        update(state, {
          options: {
            pinToShadowTrack: {
              $set: state.events.every((ev) => ev.modules.projection.enabled)
                ? PinToShadowTrackMode.FORCED
                : pinToShadowTrack,
            },
          },
        }),
      );
    },

    setElevationShowProgress(value: boolean | null) {
      batch((state) =>
        update(state, {
          options: {
            elevationShowProgress: {
              $set: value,
            },
          },
        }),
      );
    },

    setElevationShowGrid(value: boolean | null) {
      batch((state) =>
        update(state, {
          options: {
            elevationShowGrid: {
              $set: value,
            },
          },
        }),
      );
    },

    setElevationShowAxis(value: boolean | null) {
      batch((state) =>
        update(state, {
          options: {
            elevationShowAxis: {
              $set: value,
            },
          },
        }),
      );
    },

    setElevationShowPOI(value: boolean | null) {
      batch((state) =>
        update(state, {
          options: {
            elevationShowPOI: {
              $set: value,
            },
          },
        }),
      );
    },
  };
};
